import { Shippings } from '@app/pages/checkout-app/types';
import { useAppContext } from '@app/react-common/AppContext';
import Button from '@app/react-common/components/button/Button';
import { CheckoutStatusDots } from '@app/react-common/components/checkout-status/CheckoutStatusDots';
import { ConfirmationModal } from '@app/react-common/components/confirmation-modal/ConfirmationModal';
import Link from '@app/react-common/components/link/Link';
import ThreeDotMenu from '@app/react-common/components/three-dot-menu/ThreeDotMenu';
import {
  getCheckoutStatus,
  moveLiveCheckoutToTopOfTheList as sortCheckouts,
} from '@app/react-common/utils/CheckoutUtils';
import * as React from 'react';
import { useEffect, useRef, useState, useCallback } from 'react';
import { WarningBanner } from '../../../../../../../../react-common/components/banners/WarningBanner';
import useAbTests from '../../../ab-testing/react/features/list-ab-tests/hooks/useAbTests';
import useCheckouts from '../hooks/useCheckouts';
import { Checkout } from '../models/Checkout';
import { ICheckoutActions } from '../models/ICheckoutActions';
import { ISegmentActions } from '../models/ISegmentActions';
import { CheckoutFormData, CheckoutModal } from './checkout-modal/CheckoutModal';
import { CheckoutSettingsModal } from './checkout-settings-modal/CheckoutSettingsModal';
import { DeliveryMethodsList } from './delivery-methods-list/DeliveryMethodsList';
import { AllGroupsDeliveryMethodsList } from './all-groups-delivery-methods-list/AllGroupsDeliveryMethodsList';
import { Segments } from './segments/Segments';
import { CheckoutGroupSettings } from '../models/CheckoutGroupSettings';
import Tooltip from '@app/react-common/components/tooltip/Tooltip';
import { createPortal } from 'react-dom';
import {
  checkoutGroupHasAnyDiscount,
  getDeliveryMethodsWithDiscounts,
} from '@app/pages/checkout-app/utils/string.utils';
import { DiscountWarningModal } from './discount-warning-modal/DiscountWarningModal';
import { GroupsPrioritizationModal } from './groups-prioritization-modal/GroupsPrioritizationModal';
import { CheckoutGroupMetrics } from './checkout-group-metrics/CheckoutGroupMetrics';
import { PdqDateRangePicker } from '@app/react-common/components/date-range-picker/PdqDateRangePicker';
import moment from 'moment';
import { LAST_7_DAYS_PRESET } from '@app/storybook/components/datepicker/date-range-presets';

export const ALL_GROUPS_ID = 'all';

// Earliest date for which checkout group metrics are available
const METRICS_START_DATE = '2025-12-22';

interface CheckoutGroupsViewProps {}

export const CheckoutsView = ({}: CheckoutGroupsViewProps) => {
  const {
    checkouts,
    activeCheckout,
    checkoutSettings,
    activeCheckoutId,
    setActiveCheckoutId,
    checkoutActions,
    apiErrorNotification,
    setApiErrorNotification,
    segmentActions,
    fetchData,
    invalidateGroupsCache,
    refetchGroupShippings,
    fetchAllCheckoutShippings,
    isLoadingAllGroups,
  } = useCheckouts();

  const [abTestWarningBannerOpen, setAbtestWarningBannerOpen] = useState(false);
  const [rerender, setRerender] = useState(false);
  const abTestBannerRef = useRef(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [hasSidebarScrolling, setHasSidebarScrolling] = useState(false);
  const { runningAbTests, abTestActions } = useAbTests();
  const [runningAbTestCheckoutIds, setRunningAbTestCheckoutIds] = useState<number[]>([]);
  const { showToast, angularRedirect, shop$ } = useAppContext();
  const [dateRangeStart, setDateRangeStart] = useState<string>('');
  const [dateRangeEnd, setDateRangeEnd] = useState<string>('');
  const [hasUnsavedDisplayOrderChanges, setHasUnsavedDisplayOrderChanges] = useState(false);
  const [checkoutGroupIdPendingDiscardConfirmation, setCheckoutGroupIdPendingDiscardConfirmation] = useState<
    number | typeof ALL_GROUPS_ID | null
  >(null);

  const selectCheckoutGroupFromSidebar = (selectedCheckoutGroupId: number | typeof ALL_GROUPS_ID) => {
    if (selectedCheckoutGroupId === activeCheckoutId) {
      return;
    }
    if (hasUnsavedDisplayOrderChanges) {
      setCheckoutGroupIdPendingDiscardConfirmation(selectedCheckoutGroupId);
      return;
    }
    setActiveCheckoutId(selectedCheckoutGroupId);
  };

  useEffect(() => {
    if (activeCheckoutId === ALL_GROUPS_ID && checkouts.length) {
      fetchAllCheckoutShippings();
    }
  }, [activeCheckoutId, checkouts.length, fetchAllCheckoutShippings]);

  const handleDateRangeChange = useCallback((dateRange: { start: string; end: string; rangePreset: string }) => {
    if (dateRange && dateRange.start && dateRange.end) {
      setDateRangeStart(dateRange.start);
      setDateRangeEnd(dateRange.end);
    }
  }, []);

  // Reset date range when checkout group changes
  useEffect(() => {
    setDateRangeStart('');
    setDateRangeEnd('');
  }, [activeCheckoutId]);

  useEffect(() => {
    abTestActions.fetchRunningTests();
  }, []);

  useEffect(() => {
    if (!activeCheckout || !checkoutSettings) return;

    const hasRunningAbTest =
      activeCheckout.checkoutShippings.some(
        (shipping: Shippings) => shipping.enabled && !shipping.runningTest && !shipping.fallbackMethod,
      ) &&
      activeCheckout.checkoutShippings.some(
        (shipping: Shippings) => shipping.enabled && !!shipping.runningTest && !shipping.fallbackMethod,
      );
    const runTestVariantsTogether = checkoutSettings?.runTestVariantsTogether;
    const result = hasRunningAbTest && runTestVariantsTogether;

    const heightToMoveBy = abTestBannerRef.current?.clientHeight;

    if (abTestWarningBannerOpen != result) {
      if (result == true) document.querySelector('.checkout').scroll({ top: 0, behavior: 'smooth' });
      else if (heightToMoveBy) document.querySelector('.checkout').scrollBy({ top: -heightToMoveBy - 8 });
    }
    setAbtestWarningBannerOpen(result);
  }, [activeCheckout, checkoutSettings, rerender]);
  useEffect(() => {
    const activeId = getActiveIdFromUrl('checkout-groups');
    if (activeId !== null && !isNaN(activeId)) {
      setActiveCheckoutId(activeId);
    }
  }, []);

  useEffect(() => {
    if (checkouts && runningAbTests) {
      const runningAbTestCheckoutIds = runningAbTests
        .map((abTest) =>
          abTest.abTestVariants.map((variant) => variant.abTestWidgets.map((x) => x.externalId).flat()).flat(),
        )
        .flat();
      setRunningAbTestCheckoutIds(runningAbTestCheckoutIds);
    }
    if (checkouts) sortCheckouts(checkouts, runningAbTestCheckoutIds);
  }, [checkouts, runningAbTests]);
  useEffect(() => {
    const currentLocation = window.location.pathname;
    if (!currentLocation.includes('checkout-groups')) return;
    if (!activeCheckoutId) return;

    window.history.replaceState(null, '', `${currentLocation}?active=${activeCheckoutId}`);
  }, [activeCheckoutId]);
  const [modals, setModals] = useState({
    createOpen: false,
    renameOpen: false,
    deleteOpen: false,
    duplicateOpen: false,
    setAsDefaultOpen: false,
    setLiveCheckoutOpen: false,
    disableCheckoutOpen: false,
    settingsOpen: false,
    discountWarningOpen: false,
    groupsPrioritizationOpen: false,
  });

  const displayCheckouts = React.useMemo(() => {
    if (!checkoutSettings?.enableCheckoutPrioritization || !checkouts || checkouts.length === 0) {
      return checkouts || [];
    }

    const sorted = [...checkouts].sort((a, b) => {
      if (a.isDefault && !b.isDefault) {
        return -1;
      }
      if (!a.isDefault && b.isDefault) {
        return 1;
      }

      if (a.priority != null && b.priority != null) {
        return a.priority - b.priority;
      }

      if (a.priority != null && b.priority == null) {
        return -1;
      }
      if (a.priority == null && b.priority != null) {
        return 1;
      }

      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : Number.MAX_SAFE_INTEGER;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : Number.MAX_SAFE_INTEGER;
      return dateA - dateB;
    });

    return sorted;
  }, [checkouts]);

  useEffect(() => {
    const checkSidebarScrolling = () => {
      if (sidebarRef.current) {
        const hasScroll = sidebarRef.current.scrollHeight > sidebarRef.current.clientHeight;
        setHasSidebarScrolling(hasScroll);
      }
    };

    checkSidebarScrolling();

    const resizeObserver = new ResizeObserver(checkSidebarScrolling);
    if (sidebarRef.current) {
      resizeObserver.observe(sidebarRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [displayCheckouts]);

  const AddCheckoutGroupButton = (
    <div className="checkout-group-add-button-container">
      <Button
        className="checkout-group-add-button"
        onClick={() => {
          setModals((prev) => {
            return { ...prev, createOpen: true };
          });
        }}
        disabled={false}
        text={'Add checkout group'}
        size={'medium'}
        variant={'primary'}
      />
    </div>
  );

  const PrioritizationComponent = ({ appearOnSide = 'left' }: { appearOnSide?: 'left' | 'top' | 'bottom' }) => {
    if (!checkoutSettings || !displayCheckouts || displayCheckouts.length === 0) {
      return null;
    }

    return checkoutSettings.enableCheckoutPrioritization ? (
      <div
        className="checkouts-view__sidebar__manual-prioritization"
        onClick={() => {
          setModals((prev) => {
            return { ...prev, groupsPrioritizationOpen: true };
          });
        }}>
        <Tooltip
          tooltipText={'Define which checkout group takes precedence when a buyer qualifies for multiple groups.'}
          maxWidth={220}>
          <span>Prioritize Checkout Groups</span>
        </Tooltip>
        <div onClick={(e) => e.stopPropagation()}>
          <GroupsPrioritizationModal
            checkouts={displayCheckouts}
            checkoutActions={checkoutActions}
            fetchData={fetchData}
            isOpen={modals.groupsPrioritizationOpen}
            runningAbTestCheckoutIds={runningAbTestCheckoutIds}
            onClose={() => {
              setModals((prev) => {
                return { ...prev, groupsPrioritizationOpen: false };
              });
            }}
          />
        </div>
      </div>
    ) : (
      <div className="checkouts-view__sidebar__automatic-prioritization">
        <div className="checkouts-view__sidebar__automatic-prioritization__content">
          <i className="icon icon-pdqiq" />
          Automatic prioritization
        </div>
        <div className="checkouts-view__sidebar__automatic-prioritization__info-icon">
          <Tooltip
            appearOnSide={appearOnSide}
            tooltipText={
              <>
                When a buyer qualifies for multiple checkout groups, the one listed highest is used. You can enable
                manual prioritization in{' '}
                <Link
                  label="Checkout Settings"
                  onClick={() => angularRedirect('/settings/checkout')}
                  variant="monochrome"
                  size="small"
                />
              </>
            }
            maxWidth={200}>
            <i className="icon icon-more_information" />
          </Tooltip>
        </div>
      </div>
    );
  };

  return (
    <div className="checkouts-view">
      <div className="checkouts-view__left-column">
        <div
          ref={sidebarRef}
          className={`checkouts-view__sidebar ${!hasSidebarScrolling ? 'checkouts-view__sidebar--no-scroll' : ''}`}>
          {checkoutSettings?.isTestMode && (
            <WarningBanner variant="regular" title={'Shipping methods in test mode'} content={''}></WarningBanner>
          )}
          <div
            className={`checkouts-view__sidebar__item ${
              activeCheckoutId === ALL_GROUPS_ID ? 'checkouts-view__sidebar__item__active' : ''
            }`}
            onClick={() => {
              selectCheckoutGroupFromSidebar(ALL_GROUPS_ID);
            }}>
            <span className="checkouts-view__sidebar__item__text">All Groups</span>
          </div>
          <div className="checkouts-view__sidebar__divider" />
          {displayCheckouts?.map((checkout) => {
            return (
              <div
                key={checkout.id}
                className={`checkouts-view__sidebar__item ${
                  checkout.id === activeCheckoutId ? 'checkouts-view__sidebar__item__active' : ''
                }`}
                onClick={() => {
                  selectCheckoutGroupFromSidebar(checkout.id);
                }}>
                <span className="checkouts-view__sidebar__item__text">{checkout.name}</span>
                {checkout.categoryBasedShippingEnabled && (
                  <span className="checkouts-view__sidebar__item__grouped-badge">Grouped</span>
                )}
                <div className="checkouts-view__sidebar__item__status">
                  <CheckoutStatusDots status={getCheckoutStatus(checkout, runningAbTestCheckoutIds)} />
                </div>
              </div>
            );
          })}
          <div>
            {createPortal(AddCheckoutGroupButton, document.getElementById('header-portal-content'))}
            {modals.createOpen && (
              <CheckoutModal
                isOpen={modals.createOpen}
                onClose={() => {
                  setModals((prev) => {
                    return { ...prev, createOpen: false };
                  });
                }}
                onCreateSuccess={async (checkout: CheckoutFormData) => {
                  checkoutActions.createCheckout(checkout);
                  setTimeout(() => {
                    setModals((prev) => {
                      return { ...prev, createOpen: false };
                    });
                  }, 250);
                }}
                mode={'create'}
              />
            )}
          </div>
          {!hasSidebarScrolling && <PrioritizationComponent appearOnSide="left" />}
        </div>
        {hasSidebarScrolling && (
          <div className="checkouts-view__prioritization-container">
            <PrioritizationComponent appearOnSide="top" />
          </div>
        )}
      </div>
      <div className="checkouts-view__content">
        {activeCheckoutId === ALL_GROUPS_ID && displayCheckouts.length > 0 && (
          <AllGroupsDeliveryMethodsList
            key="all-groups"
            checkoutGroups={displayCheckouts}
            checkoutActions={checkoutActions}
            onDataRefresh={fetchData}
            onInvalidateGroups={invalidateGroupsCache}
            refetchGroupShippings={refetchGroupShippings}
            isLoadingGroups={isLoadingAllGroups}
          />
        )}
        {activeCheckoutId !== ALL_GROUPS_ID && activeCheckout && (
          <>
            <WarningBanner
              title={'Set up Google Shopping delivery'}
              content={
                <span>
                  Using Google Shopping? Enable PDQ rates in checkout by{' '}
                  <a
                    className="react-banner__link"
                    href="https://support.prettydamnquick.com/en/articles/6183367-setting-up-delivery-methods-for-google-shopping"
                    target="blank">
                    following this guide
                  </a>
                </span>
              }
              sessionKey="pdq-google-banner-closed"
            />
            {abTestWarningBannerOpen && (
              <WarningBanner
                forwardedRef={abTestBannerRef}
                title={'Notice: A/B tests on individual methods are not running due to a general setting'}
                content={
                  <>
                    <span>
                      One or more of your checkout methods doesn't have the A/B test enabled, so the B variant of that
                      method isn't presented at checkout (working as its supposed to) you can either change it in
                      the&nbsp;
                      <a
                        className="react-banner__link"
                        onClick={() => {
                          angularRedirect('/settings/checkout');
                        }}>
                        general settings
                      </a>
                      &nbsp;or enable the B variant on the other checkouts
                    </span>
                  </>
                }
              />
            )}
            <div className="checkouts-view__content__header">
              <div>
                <h1 className="checkouts-view__content__header__title">{activeCheckout.name}</h1>
                <p className="checkouts-view__content__header__desc">{activeCheckout.description}</p>
              </div>
              <div className="checkouts-view__content__header__actions">
                <div className="checkouts-view__content__header__actions__date-picker">
                  <PdqDateRangePicker
                    key={`date-picker-${activeCheckout.id}`}
                    positionRight={true}
                    background="light"
                    minDate={
                      moment.utc(activeCheckout.createdAt).isBefore(moment.utc(METRICS_START_DATE))
                        ? METRICS_START_DATE
                        : activeCheckout.createdAt
                    }
                    defaultPreset={LAST_7_DAYS_PRESET}
                    onApply={handleDateRangeChange}
                  />
                </div>
                <Button
                  className="checkouts-view__content__header__actions__howitworks"
                  onClick={() => {
                    window.open(
                      'https://support.prettydamnquick.com/en/articles/9209096-how-to-set-up-a-new-checkout',
                      '_blank',
                    );
                  }}
                  disabled={false}
                  text={'How it works'}
                  size={'medium'}
                  iconUrl="/assets/icons/external.svg"
                  variant={'tertiary'}
                />
                <div className="checkouts-view__content__header__actions__threedot">
                  <ThreeDotMenu
                    items={getCheckoutMenuItems(
                      setModals,
                      checkouts,
                      activeCheckout,
                      checkoutActions,
                      runningAbTestCheckoutIds,
                      segmentActions,
                      showToast,
                    )}
                  />
                </div>
                <CheckoutModal
                  isOpen={modals.renameOpen}
                  onClose={() => {
                    setModals((prev) => {
                      return { ...prev, renameOpen: false };
                    });
                  }}
                  onRenameSuccess={async (checkout: CheckoutFormData) => {
                    checkoutActions.renameCheckout(activeCheckout.id, checkout);
                    setModals((prev) => {
                      return { ...prev, renameOpen: false };
                    });
                  }}
                  checkoutName={activeCheckout.name}
                  checkoutDescription={activeCheckout.description}
                  mode={'rename'}
                />
                <CheckoutModal
                  isOpen={modals.deleteOpen}
                  onClose={() => {
                    setModals((prev) => {
                      return { ...prev, deleteOpen: false };
                    });
                  }}
                  onDeleteSuccess={async () => {
                    checkoutActions.deleteCheckout(activeCheckout.id);
                    setModals((prev) => {
                      return { ...prev, deleteOpen: false };
                    });
                  }}
                  mode={'delete'}
                />
                <CheckoutModal
                  isOpen={modals.duplicateOpen}
                  onClose={() => {
                    setModals((prev) => {
                      return { ...prev, duplicateOpen: false };
                    });
                  }}
                  onDuplicateSuccess={async (checkout: CheckoutFormData) => {
                    checkoutActions.duplicateCheckout(activeCheckout.id, checkout);
                    setTimeout(() => {
                      setModals((prev) => {
                        return { ...prev, duplicateOpen: false };
                      });
                    }, 250);
                  }}
                  checkoutName={`Copy of '${activeCheckout.name}'`}
                  checkoutDescription={activeCheckout.description}
                  mode={'duplicate'}
                />
                {modals.setAsDefaultOpen && (
                  <ConfirmationModal
                    title={'Are you sure?'}
                    buttonText={'Yes, set as default'}
                    mainContent={
                      'This checkout has no segments and will replace your current default checkout. Your current default checkout will automatically be marked as disabled. This action should be taken with caution.'
                    }
                    isOpen={modals.setAsDefaultOpen}
                    onClose={() => {
                      setModals((prev) => {
                        return { ...prev, setAsDefaultOpen: false };
                      });
                    }}
                    onSuccess={() => {
                      checkoutActions.setDefaultCheckout(activeCheckout.id);
                      setModals((prev) => {
                        return { ...prev, setAsDefaultOpen: false };
                      });
                    }}
                  />
                )}

                {modals.settingsOpen && (
                  <CheckoutSettingsModal
                    shippingMethodGroupId={activeCheckout.id}
                    isOpen={modals.settingsOpen}
                    onClose={() => {
                      setModals((prev) => {
                        return { ...prev, settingsOpen: false };
                      });
                    }}
                    onSaveCheckoutSettingsSuccess={async (checkoutGroupSettings: CheckoutGroupSettings) => {
                      checkoutActions.saveCheckoutMethodGroupSettings(activeCheckout.id, checkoutGroupSettings);
                      setTimeout(() => {
                        setModals((prev) => {
                          return { ...prev, settingsOpen: false };
                        });
                      }, 250);
                    }}
                  />
                )}

                {modals.discountWarningOpen && (
                  <DiscountWarningModal
                    isOpen={modals.discountWarningOpen}
                    onClose={() => {
                      setModals((prev) => {
                        return { ...prev, discountWarningOpen: false };
                      });
                    }}
                    onContinue={() => {
                      setModals((prev) => {
                        return { ...prev, discountWarningOpen: false, duplicateOpen: true };
                      });
                    }}
                    deliveryMethodsWithDiscounts={getDeliveryMethodsWithDiscounts(activeCheckout)}
                  />
                )}
              </div>
            </div>
            <CheckoutGroupMetrics
              checkoutGroupId={activeCheckout.id}
              isLive={activeCheckout.isLive}
              startDate={dateRangeStart}
              endDate={dateRangeEnd}
            />
            <Segments
              segments={activeCheckout?.checkoutShippingMethodGroupSegments}
              actions={segmentActions}
              checkout={activeCheckout}
            />
            <div className="checkouts-view__content__delivery-methods">
              {activeCheckout && (
                <DeliveryMethodsList
                  key={activeCheckout.id}
                  checkoutGroups={checkouts}
                  activeCheckout={activeCheckout}
                  checkoutActions={checkoutActions}
                  setRerender={setRerender}
                  onDataRefresh={fetchData}
                  onInvalidateGroups={invalidateGroupsCache}
                  refetchGroupShippings={refetchGroupShippings}
                  fetchAllCheckoutShippings={fetchAllCheckoutShippings}
                  isLoadingAllGroups={isLoadingAllGroups}
                  onUnsavedDisplayOrderChangesChange={setHasUnsavedDisplayOrderChanges}
                />
              )}
            </div>
          </>
        )}
      </div>
      <ConfirmationModal
        title={'Are you sure?'}
        buttonText={'Discard changes'}
        mainContent={'You have unsaved delivery method order changes. Switching groups will discard them.'}
        isOpen={checkoutGroupIdPendingDiscardConfirmation != null}
        onClose={() => {
          setCheckoutGroupIdPendingDiscardConfirmation(null);
        }}
        onSuccess={() => {
          setActiveCheckoutId(checkoutGroupIdPendingDiscardConfirmation);
          setCheckoutGroupIdPendingDiscardConfirmation(null);
        }}
      />
      {apiErrorNotification && (
        <ConfirmationModal
          title={apiErrorNotification.title}
          buttonText={'Ok'}
          mainContent={apiErrorNotification.content}
          isOpen={apiErrorNotification != null}
          onClose={() => {
            setApiErrorNotification(null);
          }}
          onSuccess={() => {
            setApiErrorNotification(null);
          }}
        />
      )}
    </div>
  );
};

function getCheckoutMenuItems(
  setModals,
  checkouts: Checkout[],
  activeCheckout: Checkout,
  checkoutActions: ICheckoutActions,
  runningAbTestCheckoutIds: number[],
  segmentActions: ISegmentActions,
  showToast,
) {
  const checkoutMenuItems = [];
  if (!activeCheckout.isLive) {
    checkoutMenuItems.push({
      label: 'Set live',
      onClick: () => {
        if (activeCheckout.checkoutShippingMethodGroupSegments?.length === 0 && activeCheckout.isDefault === false) {
          setModals((prev) => {
            return { ...prev, setAsDefaultOpen: true };
          });
        } else {
          checkoutActions.toggleLiveCheckout(activeCheckout.id, true);
        }
      },
    });
  } else {
    checkoutMenuItems.push({
      label: 'Disable',
      onClick: () => {
        checkoutActions.toggleLiveCheckout(activeCheckout.id, false);
      },
    });
  }
  checkoutMenuItems.push({
    label: 'Add segment',
    onClick: () => {
      if (activeCheckout.isLive) {
        showToast({ message: "You can't add segments to a live checkout", mode: 'warning' });
      } else {
        segmentActions.createSegment({
          name: 'Segment name',
          rules: null,
          matchAllRules: true,
          shippingMethodGroupId: activeCheckout.id,
        });
      }
    },
  });

  checkoutMenuItems.push({
    label: 'Duplicate',
    onClick: () => {
      if (checkoutGroupHasAnyDiscount(activeCheckout)) {
        setModals((prev) => {
          return { ...prev, discountWarningOpen: true };
        });
      } else {
        setModals((prev) => {
          return { ...prev, duplicateOpen: true };
        });
      }
    },
  });

  checkoutMenuItems.push(
    {
      label: 'Rename',
      onClick: () => {
        setModals((prev) => {
          return { ...prev, renameOpen: true };
        });
      },
    },
    {
      label: 'Settings',
      onClick: () => {
        setModals((prev) => {
          return { ...prev, settingsOpen: true };
        });
      },
    },
  );

  checkoutMenuItems.push({
    label: 'Copy test ID',
    onClick: () => {
      const testId = `TestCheckoutGroup-${activeCheckout.id.toString()}`;
      if (!navigator?.clipboard) {
        alert(`Your browser does not support copying to clipboard. Test ID is: ${testId}`);
        return;
      }
      navigator.clipboard.writeText(testId).then(() => {
        showToast({ message: 'Checkout ID copied to clipboard', mode: 'success' });
      });
    },
  });

  if (!activeCheckout.isLive) {
    checkoutMenuItems.push({
      label: 'Delete',
      onClick: () => {
        setModals((prev) => {
          return { ...prev, deleteOpen: true };
        });
      },
      variant: 'danger',
    });
  }

  return checkoutMenuItems;
}

const getActiveIdFromUrl = (pathSegment: string): number | null => {
  const currentLocation = window.location.pathname;
  if (!currentLocation.includes(pathSegment)) return null;

  const urlParams = new URLSearchParams(window.location.search);
  const activeIdFromUrl = urlParams.get('active');
  if (activeIdFromUrl) {
    return Number(activeIdFromUrl);
  }
  return null;
};
